import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import {
    AgreementKind, BenchmarkRequestError, BenchmarkRun, DispositionMatrixResponse, RunResultsResponse,
    SuppliedResultRow, benchmarkService
} from '../../../../services/benchmarkService';
import { PanelError, SectionCard } from '../../BenchmarkPrimitives';
import { keepingNavState } from '../../../../utils/searchParamsNavigation';
import { WithheldIndex } from '../../withheldMetrics';
import ResultsSummaryStrip from './ResultsSummaryStrip';
import AgreementMatrix from './AgreementMatrix';
import SuppliedResultsTable, { RESULT_FILTER_KEYS, ResultFilters } from './SuppliedResultsTable';
import { SUPPLIED_RUN_RETENTION } from '../suppliedSampleCopy';
import { dsVar } from '../../../../theme/tokens';

/** Older supplied runs are deleted as new ones arrive, results and all. */
export const RESULTS_RETENTION_NOTE =
    `Only the ${SUPPLIED_RUN_RETENTION} most recent supplied-sample runs per project are kept. Download the results to keep them.`;

/** Paging lives in the query string under its own names, so it does not collide with other tabs' tables. */
const PAGE_KEY = 'results_page';
const SIZE_KEY = 'results_limit';
const DEFAULT_PAGE_SIZE = 50;

interface SuppliedResultsTabProps {
    run: BenchmarkRun;
    matrix: DispositionMatrixResponse | null;
    matrixLoading?: boolean;
    matrixError?: string;
    onRetryMatrix?: () => void;
    withheld: WithheldIndex;
    onRowSelect: (row: SuppliedResultRow) => void;
    selectedCaseRef?: string | null;
}

/**
 * What a user who benchmarked their own alerts wants to know, top to bottom:
 * how many cases were scored and how often the platform agreed with their
 * tiers, where it disagreed, and — alert by alert — what it did, with why a
 * click away. The full executive dashboard follows on its own tabs.
 */
const SuppliedResultsTab: React.FC<SuppliedResultsTabProps> = ({
    run, matrix, matrixLoading, matrixError, onRetryMatrix, withheld, onRowSelect, selectedCaseRef
}) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const filters: ResultFilters = useMemo(() => {
        const next: ResultFilters = {};
        RESULT_FILTER_KEYS.forEach((key) => {
            const value = searchParams.get(key);
            if (value) next[key] = value;
        });
        return next;
    }, [searchParams]);
    const page = Math.max(parseInt(searchParams.get(PAGE_KEY) || '0', 10) || 0, 0);
    const pageSize = parseInt(searchParams.get(SIZE_KEY) || '', 10) || DEFAULT_PAGE_SIZE;

    /** The filters as the API names them. */
    const query = useMemo(() => ({
        agreement: filters.agreement as AgreementKind | undefined,
        outcome: filters.outcome,
        platform_tier: filters.platform_tier,
        scenario_id: filters.scenario_id
    }), [filters]);

    const [results, setResults] = useState<RunResultsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        benchmarkService.getRunResults(run.runKey, { ...query, limit: pageSize, offset: page * pageSize })
            .then((data) => { if (!cancelled) setResults(data); })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load the results.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [run.runKey, query, page, pageSize, attempt]);

    const updateParams = useCallback((apply: (next: URLSearchParams) => void) => {
        const next = new URLSearchParams(searchParams);
        apply(next);
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    }, [searchParams, setSearchParams, location.state]);

    const changeFilters = (nextFilters: ResultFilters) => updateParams((next) => {
        RESULT_FILTER_KEYS.forEach((key) => {
            const value = nextFilters[key];
            if (value) next.set(key, value); else next.delete(key);
        });
        next.delete(PAGE_KEY);
    });

    const exportCsv = async () => {
        setExporting(true);
        setExportError(null);
        try {
            await benchmarkService.downloadRunResultsCsv(run.runKey, query);
        } catch (err) {
            setExportError(err instanceof BenchmarkRequestError ? err.message : 'The download failed.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box className="flex flex-col gap-6">
            <ResultsSummaryStrip
                run={run}
                summary={results?.summary ?? null}
                matrix={matrix}
                withheld={withheld}
                loading={matrixLoading || (loading && !results)}
            />

            <AgreementMatrix matrix={matrix} loading={matrixLoading} error={matrixError} onRetry={onRetryMatrix} />

            <SectionCard
                title="Your alerts, one by one"
                subtitle="The tier you gave each alert beside the tier the platform gave its case. Select an alert to see why."
                icon={<ListAltOutlinedIcon fontSize="small" />}
                actions={
                    <Button
                        onClick={exportCsv}
                        disabled={exporting}
                        variant="contained"
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem',
                            backgroundColor: dsVar('accent'), color: dsVar('on-accent'), borderRadius: '8px',
                            '&:hover': { backgroundColor: '#F5D149' },
                            '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') }
                        }}
                    >
                        {exporting ? 'Preparing…' : 'Download results (CSV)'}
                    </Button>
                }
            >
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mb: 2 }}>{RESULTS_RETENTION_NOTE}</Typography>
                {exportError && <Box sx={{ mb: 2 }}><PanelError message={exportError} /></Box>}
                {error ? (
                    <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />
                ) : (
                    <SuppliedResultsTable
                        rows={results?.alerts ?? []}
                        summary={results?.summary ?? null}
                        total={results?.pagination.total ?? 0}
                        page={page}
                        pageSize={pageSize}
                        loading={loading}
                        filters={filters}
                        onFiltersChange={changeFilters}
                        onPageChange={(p) => updateParams((next) => next.set(PAGE_KEY, String(p)))}
                        onPageSizeChange={(size) => updateParams((next) => { next.set(SIZE_KEY, String(size)); next.delete(PAGE_KEY); })}
                        onRowSelect={onRowSelect}
                        selectedCaseRef={selectedCaseRef}
                    />
                )}
            </SectionCard>
        </Box>
    );
};

export default SuppliedResultsTab;
